import 'bootstrap/dist/css/bootstrap.min.css';
import { useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { AuthContext } from '../../context/AuthContext';
import SuccessModal from '../../components/modals/SuccessModal';
import { useNotificationModal } from '../../hooks/useNotificationModal';
import CountryCodeSelect from '../../components/CountryCodeSelect';
import CountryDropdown from "../../components/CountryDropdown";
import '../../styles/CompanySettings.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function CompanySettings() {
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError
  } = useNotificationModal();
  
  const [formData, setFormData] = useState({
    company_code: "",
    company_name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    company_logo: null,
    currency: "LKR",
    company_phone: "",
    countryCode: "+",
    company_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const { userData } = useContext(AuthContext);

  const company_code = userData?.company_code;
    
  // Currency options from API
  const [currencies, setCurrencies] = useState([
    { value: 'LKR', label: 'LKR (₨) - Sri Lankan Rupee' }
  ]);

  useEffect(() => {
    async function fetchCurrencies() {
      try {
        // Use openexchangerates.org/api/currencies.json (no access key required for currency list)
        const res = await fetch(`${BASE_URL}/api/admin/currencies/get-all-currency-symbols`);
        if (!res.ok) throw new Error('Currency API error');
        const data = await res.json();
        if (data && data.currencies) {
          setCurrencies(data.currencies);
        } else {
          throw new Error('Currency API returned no symbols');
        }
      } catch (err) {
        // fallback to default
        setCurrencies([
          { value: 'LKR', label: 'LKR (₨) - Sri Lankan Rupee' },
          { value: 'GBP', label: 'GBP (£) - British Pound Sterling' },
          { value: 'USD', label: 'USD ($) - US Dollar' },
          { value: 'EUR', label: 'EUR (€) - Euro' },
          { value: 'AED', label: 'AED (د.إ) - UAE Dirham' }
        ]);
      }
    }
    fetchCurrencies();
  }, []);

  useEffect(() => {
    async function fetchCompany() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${BASE_URL}/api/admin/companies-by-code/${company_code}`);
        if (!res.ok) throw new Error('Failed to fetch company');
        const data = await res.json();
        const company = data.company || {};
        setFormData({
          company_code: company.company_code || '',
          company_name: company.company_name || '',
          address_line_1: company.address_line1 || '',
          address_line_2: company.address_line2 || '',
          city: company.city || '',
          state: company.state || '',
          country: company.country || '',
          postal_code: company.postal_code || '',
          company_logo: null,
          currency: company.currency || 'LKR',
          company_phone: String(company.company_phone || ''),
          countryCode: company.company_country_code || '+',
          company_email: company.company_email || ''
        });
        if (company.company_logo) {
          setCurrentLogo(company.company_logo);
        }
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchCompany();
  }, [company_code]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCurrencyChange = selected => {
    setFormData(prev => ({ ...prev, currency: selected.value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, company_logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const cleanPhone = formData.company_phone ? formData.company_phone.replace(/\D/g, '') : '';
      
      const companyFormData = new FormData();
      companyFormData.append('company_name', formData.company_name);
      companyFormData.append('address_line_1', formData.address_line_1);
      companyFormData.append('address_line_2', formData.address_line_2);
      companyFormData.append('city', formData.city);
      companyFormData.append('state', formData.state);
      companyFormData.append('country', formData.country);
      companyFormData.append('postal_code', formData.postal_code);
      companyFormData.append('currency', formData.currency);
      companyFormData.append('company_country_code', formData.countryCode);
      companyFormData.append('company_phone', cleanPhone);
      companyFormData.append('company_email', formData.company_email);
      if (formData.company_logo) {
        companyFormData.append('company_logo', formData.company_logo);
      }
      const res = await fetch(`${BASE_URL}/api/admin/companies-by-code/${company_code}`, {
        method: 'PUT',
        body: companyFormData
      });
      if (!res.ok) throw new Error('Update failed');
      showSuccess('Company updated successfully!', 'Success');
      setSuccess('Company updated successfully!');
      setLogoPreview(null);
      // Optionally refetch company data
    } catch (err) {
      showError(err.message || 'Failed to update company settings', 'Error');
      setError(err.message);
    }
  };

  return (
    <Container fluid>
      <Card className="register-card">
        <Card.Body>
          <div className="com-settings-header">
            <h1>Company Settings </h1>
          </div>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="secondary" />
            </div>
          ) : (
            <Form className="register-form" onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Code</Form.Label>
                    <Form.Control
                      name="company_code"
                      value={formData.company_code}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency</Form.Label>
                    <Select
                      name="currency"
                      value={currencies.find(
                        (c) => c.value === formData.currency,
                      )}
                      onChange={handleCurrencyChange}
                      options={currencies}
                      isSearchable={true}
                      placeholder="Select Currency"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Company Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="Enter company name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Company Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <CountryCodeSelect
                        value={formData.countryCode}
                        onChange={(code) =>
                          setFormData((prev) => ({
                            ...prev,
                            countryCode: code,
                          }))
                        }
                        placeholder="Code"
                        isRequired={true}
                        width="100px"
                      />
                      <Form.Control
                        type="text"
                        name="company_phone"
                        value={formData.company_phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            company_phone: value,
                          }));
                        }}
                        placeholder="Enter phone number"
                        required
                        style={{ flex: 1 }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Company Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="company_email"
                      value={formData.company_email}
                      onChange={handleChange}
                      placeholder="Enter company email"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Address Line 1 <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="address_line_1"
                      value={formData.address_line_1}
                      onChange={handleChange}
                      placeholder="Enter address line 1"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 2 </Form.Label>
                    <Form.Control
                      type="text"
                      name="address_line_2"
                      value={formData.address_line_2}
                      onChange={handleChange}
                      placeholder="Enter address line 2"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter city"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="Enter state"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Country <span className="text-danger">*</span>{" "}
                    </Form.Label>
                    <CountryDropdown
                      value={formData.country || ""}
                      onChange={(country) =>
                        setFormData((prev) => ({ ...prev, country: country }))
                      }
                      placeholder="Select country"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      placeholder="Enter postal code"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Company Logo{" "}
                      <small className="text-muted">
                        (Leave empty to keep current logo)
                      </small>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="company_logo"
                      onChange={handleFileChange}
                      accept="image/*"
                    />
                    <Form.Text className="text-muted">
                      Please upload a company logo (PNG, JPG, JPEG)
                    </Form.Text>
                    {/* Show current logo */}
                    {currentLogo && !logoPreview && (
                      <div className="mt-2 text-center">
                        <p className="small text-muted">Current Logo:</p>
                        <img
                          src={`${BASE_URL}/uploads/company_logos/${currentLogo}`}
                          alt="Current Company Logo"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "100px",
                            objectFit: "contain",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            padding: "5px",
                          }}
                        />
                      </div>
                    )}
                    {/* Show new logo preview */}
                    {logoPreview && (
                      <div className="mt-2 text-center">
                        <p className="small text-muted">New Logo Preview:</p>
                        <img
                          src={logoPreview}
                          alt="Company Logo Preview"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "100px",
                            objectFit: "contain",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            padding: "5px",
                          }}
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="text-center">
                <Button
                  type="submit"
                  className="register-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating Company...
                    </>
                  ) : (
                    "Update Company"
                  )}
                </Button>
              </div>
              {error && (
                <Alert variant="danger" className="mt-3 text-center">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" className="mt-3 text-center">
                  {success}
                </Alert>
              )}
            </Form>
          )}
        </Card.Body>
      </Card>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        variant="success"
        modalTitle={successTitle}
        message={successMessage}
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        variant="error"
        modalTitle={errorTitle}
        message={errorMessage}
      />
    </Container>
  );
}
